// Instant kill switch for the 3D hero, read at REQUEST time.
//
// `VITE_LANDING_VISUALS` (capability-policy.js's `capabilities.enabled`) is a
// Vite `import.meta.env` substitution baked into the bundle at build time -
// flipping it means a full rebuild + redeploy, which does not satisfy "an
// instant server/config kill switch that disables 3D without redeploying
// semantic content." This module is the runtime half of that switch: it
// fetches a same-origin JSON endpoint. On Cloudflare Pages this is served by
// functions/visual-config.json.js and reads D1 at request time; the retained
// Netlify edge handler serves the same contract during the migration window.
//
// Pure of ambient state per this directory's convention: the caller supplies
// `fetchImpl` and (optionally) an AbortController factory rather than this
// module reaching for `window.fetch`/`AbortController` itself.
//
// Fails SAFE, unconditionally: a missing fetch implementation, a rejected
// fetch, a non-2xx response, a timeout/abort, malformed JSON, or an
// `enabled` field that is not strictly boolean `true` all resolve to
// `false`. Nothing in this module can ever resolve to "enabled" except an
// explicit, well-formed `{ "enabled": true }` response body.
export async function fetchRuntimeVisualFlag({
  fetchImpl,
  url = '/visual-config.json',
  // Four seconds accommodates both the Cloudflare D1 lookup and the retained
  // Netlify Blob fallback during migration. A shorter 1.5s deadline aborted
  // valid production responses before they could answer, leaving no canvas.
  timeoutMs = 4000,
  createAbortController,
} = {}) {
  if (typeof fetchImpl !== 'function') return false;

  const controller = typeof createAbortController === 'function'
    ? createAbortController()
    : (typeof AbortController === 'function' ? new AbortController() : undefined);

  // Signal-only cooperation isn't enough to fail safe: a fetch stand-in (or
  // a real request against a slow/misbehaving edge) that never settles must
  // not hang this call forever. Race the request against its own timeout so
  // "timed out" always resolves the flag to false on schedule, whether or
  // not the callee actually honours AbortController.
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => {
      controller?.abort();
      resolve({ timedOut: true });
    }, timeoutMs);
  });

  try {
    const outcome = await Promise.race([
      (async () => {
        const response = await fetchImpl(url, {
          signal: controller?.signal,
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        return { response };
      })(),
      timeout,
    ]);
    if (outcome.timedOut || !outcome.response || outcome.response.ok !== true) return false;
    const body = await outcome.response.json();
    return body?.enabled === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
